import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Trash2, Plus, AlertCircle, Check, Copy, Info, Settings } from 'lucide-react';
import SnowflakeSsoLoginButton from '@/components/tenant-config/SnowflakeSsoLoginButton';
import { showToast } from '@/services/toast.service';
import TenantConfigService, {
  SnowflakeSsoUser,
  SnowflakeSsoConfig,
} from '@/services/tenantConfig.service';
import { useFeatureFlag } from '@/services/featureFlag.service';

interface SnowflakeSsoCardProps {
  tenantConfigId: string;
}

export default function SnowflakeSsoCard({ tenantConfigId }: SnowflakeSsoCardProps) {
  const canEditSso = useFeatureFlag('edit-sf-sso-configuration', false);

  const [ssoConfig, setSsoConfig] = useState<SnowflakeSsoConfig | null>(null);
  const [savedConfig, setSavedConfig] = useState<SnowflakeSsoConfig | null>(null);
  const [isHosted, setIsHosted] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [scriptSql, setScriptSql] = useState('');
  const [showConfig, setShowConfig] = useState(false);

  // Any authenticated user: check if they can login via SSO
  const { data: ssoStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['snowflakeSsoStatus', tenantConfigId],
    queryFn: () => TenantConfigService.getInstance().getSnowflakeSsoStatus(tenantConfigId),
    enabled: !!tenantConfigId,
    staleTime: 30 * 1000,
    retry: false,
  });

  const canLogin = ssoStatus?.can_login ?? false;

  // Fetch full SSO config (editors only)
  const { data: ssoData, isLoading: loadingSso, refetch: refetchSso } = useQuery({
    queryKey: ['snowflakeSsoConfig', tenantConfigId],
    queryFn: () => TenantConfigService.getInstance().getSnowflakeSsoConfig(tenantConfigId),
    enabled: !!tenantConfigId && canEditSso,
    staleTime: 30 * 1000,
    retry: false,
  });

  // Fetch tenant users for the user picker
  const { data: tenantUsers } = useQuery({
    queryKey: ['tenantUsers'],
    queryFn: () => TenantConfigService.getInstance().getTenantUsers(),
    enabled: canEditSso,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (ssoData) {
      setSsoConfig(ssoData.record);
      setSavedConfig(ssoData.record);
      setIsHosted(ssoData.is_hosted);
      setAvailableRoles(ssoData.available_roles || []);
      setSyncError(ssoData.record.sync_error);
    }
  }, [ssoData]);

  const hasSynced = !!ssoConfig?.last_synced_at;

  // Dirty state tracking
  const isDirty = useMemo(() => {
    if (!ssoConfig || !savedConfig) return false;
    return JSON.stringify(ssoConfig.users) !== JSON.stringify(savedConfig.users);
  }, [ssoConfig, savedConfig]);

  const userChangeStatus = useMemo(() => {
    const map = new Map<string, 'new' | 'changed'>();
    if (!ssoConfig || !savedConfig) return map;
    const savedByEmail = new Map(savedConfig.users.map((u) => [u.email.toLowerCase(), u]));
    for (const u of ssoConfig.users) {
      const key = u.email.toLowerCase();
      const saved = savedByEmail.get(key);
      if (!saved) {
        map.set(key, 'new');
      } else if (
        JSON.stringify(u.snowflake_roles) !== JSON.stringify(saved.snowflake_roles) ||
        u.default_role !== saved.default_role
      ) {
        map.set(key, 'changed');
      }
    }
    return map;
  }, [ssoConfig, savedConfig]);

  const addUser = useCallback((email: string) => {
    if (!ssoConfig) return;
    const existingUser = tenantUsers?.find((u) => u.email === email);
    const newUser: SnowflakeSsoUser = {
      email,
      name: existingUser?.name ?? email.split('@')[0],
      frontegg_user_id: existingUser?.id ?? '',
      snowflake_roles: ['PLIABLE_DATA_ROLE'],
      default_role: 'PLIABLE_DATA_ROLE',
    };
    setSsoConfig({
      ...ssoConfig,
      users: [...ssoConfig.users, newUser],
    });
  }, [ssoConfig, tenantUsers]);

  const removeUser = useCallback((email: string) => {
    if (!ssoConfig) return;
    setSsoConfig({
      ...ssoConfig,
      users: ssoConfig.users.filter((u) => u.email !== email),
    });
  }, [ssoConfig]);

  const updateUserRoles = useCallback((email: string, roles: string[]) => {
    if (!ssoConfig) return;
    setSsoConfig({
      ...ssoConfig,
      users: ssoConfig.users.map((u) =>
        u.email === email ? { ...u, snowflake_roles: roles } : u
      ),
    });
  }, [ssoConfig]);

  const updateUserDefaultRole = useCallback((email: string, role: string) => {
    if (!ssoConfig) return;
    setSsoConfig({
      ...ssoConfig,
      users: ssoConfig.users.map((u) =>
        u.email === email ? { ...u, default_role: role } : u
      ),
    });
  }, [ssoConfig]);

  const toggleRole = useCallback((email: string, role: string) => {
    if (!ssoConfig) return;
    const u = ssoConfig.users.find((u) => u.email === email);
    if (!u) return;
    const hasRole = u.snowflake_roles.includes(role);
    const newRoles = hasRole
      ? u.snowflake_roles.filter((r) => r !== role)
      : [...u.snowflake_roles, role];
    updateUserRoles(email, newRoles);
  }, [ssoConfig, updateUserRoles]);

  const saveConfig = useCallback(async () => {
    if (!ssoConfig || !tenantConfigId) return;
    await TenantConfigService.getInstance().updateSnowflakeSsoConfig(tenantConfigId, {
      enabled: true,
      users: ssoConfig.users,
    });
  }, [ssoConfig, tenantConfigId]);

  // Hosted: save + auto-sync via background job
  const saveAndSync = useCallback(async () => {
    if (!ssoConfig || !tenantConfigId) return;

    setSaving(true);
    setSyncing(true);
    setSyncError(null);

    try {
      await saveConfig();
      await TenantConfigService.getInstance().syncSnowflakeSso(tenantConfigId);
      showToast.success('SAML config synced to Snowflake');
      setShowConfig(false);
      const freshData = await refetchSso();
      if (freshData.data) {
        setSavedConfig(freshData.data.record);
      }
      refetchStatus();
    } catch (err: any) {
      const msg = err.message || 'Sync failed';
      setSyncError(msg);
      showToast.error('Sync failed', { description: msg });
    } finally {
      setSaving(false);
      setSyncing(false);
    }
  }, [ssoConfig, tenantConfigId, saveConfig, refetchSso, refetchStatus]);

  // Non-hosted: save config then generate SQL script for the user
  const saveAndGenerateScript = useCallback(async () => {
    if (!ssoConfig || !tenantConfigId) return;

    setSaving(true);
    setGeneratingScript(true);
    setSyncError(null);

    try {
      await saveConfig();
      // Reset saved state after successful save
      if (ssoConfig) setSavedConfig({ ...ssoConfig });
      const script = await TenantConfigService.getInstance().generateSsoScript(tenantConfigId);
      setScriptSql(script);
      setShowScriptModal(true);
    } catch (err: any) {
      const msg = err.message || 'Failed to generate script';
      setSyncError(msg);
      showToast.error('Script generation failed', { description: msg });
    } finally {
      setSaving(false);
      setGeneratingScript(false);
    }
  }, [ssoConfig, tenantConfigId, saveConfig]);

  // After the user says they've run the script, mark config as synced
  const markAsSynced = useCallback(async () => {
    if (!tenantConfigId) return;
    try {
      await TenantConfigService.getInstance().updateSnowflakeSsoConfig(tenantConfigId, {
        enabled: true,
        last_synced_at: new Date().toISOString(),
      });
      setShowScriptModal(false);
      setShowConfig(false);
      showToast.success('SSO marked as configured');
      refetchSso();
      refetchStatus();
    } catch (err: any) {
      showToast.error('Failed to update config');
    }
  }, [tenantConfigId, refetchSso, refetchStatus]);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(scriptSql);
    showToast.success('SQL copied to clipboard');
  }, [scriptSql]);

  // Available users = tenant users not already added
  const availableUsers = (tenantUsers ?? []).filter(
    (tu) => !ssoConfig?.users?.some((su) => su.email === tu.email)
  );

  if (!tenantConfigId) return null;

  // Non-superuser who isn't in the SSO list: show a "contact your rep" message
  if (!canEditSso && !canLogin) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Snowflake SSO</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Contact your account rep about setting up Snowflake SSO access.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Script Modal — non-hosted accounts */}
      <Dialog open={showScriptModal} onOpenChange={setShowScriptModal}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>SSO Setup Script</DialogTitle>
            <DialogDescription>
              Run this script in your Snowflake account as ACCOUNTADMIN to enable SAML SSO.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-md">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Log into your Snowflake account and run the following script in a new worksheet.
                Make sure "All Queries" is checked. When you're done, close this window and
                click "Login to Snowflake" to test.
              </p>
            </div>

            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="absolute right-2 top-2 z-10"
                onClick={copyToClipboard}
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </Button>
              <pre className="p-4 bg-muted rounded-md overflow-auto max-h-[400px] text-sm font-mono">
                {scriptSql}
              </pre>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowScriptModal(false)}>
                Close
              </Button>
              <Button onClick={markAsSynced}>
                I've Run the Script
              </Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Snowflake SSO</CardTitle>
              <CardDescription>
                {canEditSso
                  ? 'Configure SAML-based single sign-on to let team members access Snowflake directly from Pliable.'
                  : 'Log into Snowflake using your Pliable identity.'}
              </CardDescription>
            </div>
            {canEditSso && hasSynced && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowConfig(!showConfig)}
                title="Edit SSO configuration"
              >
                <Settings className="h-5 w-5 text-muted-foreground" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Login button — shown to any authorized user */}
          {canLogin && (
            <SnowflakeSsoLoginButton tenantConfigId={tenantConfigId} />
          )}

          {/* Collapsed summary — superuser, already synced, config hidden */}
          {canEditSso && hasSynced && !showConfig && (
            <div className="space-y-2">
              {!syncError && !ssoConfig?.sync_error && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span>
                    {ssoConfig!.users.length} user{ssoConfig!.users.length !== 1 ? 's' : ''} configured
                    &middot; Last synced {new Date(ssoConfig!.last_synced_at!).toLocaleString()}
                  </span>
                </div>
              )}
              {(syncError || ssoConfig?.sync_error) && (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950 rounded-md">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-red-800 dark:text-red-200">
                    {syncError || ssoConfig?.sync_error}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Config UI — superuser only, shown when not yet synced OR gear toggled open */}
          {canEditSso && (!hasSynced || showConfig) && (
            <>
              {loadingSso ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Sync status */}
                  {hasSynced && !syncError && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950 rounded-md">
                      <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <span className="text-sm text-green-800 dark:text-green-200">
                        Last synced: {new Date(ssoConfig!.last_synced_at!).toLocaleString()}
                      </span>
                    </div>
                  )}

                  {(syncError || ssoConfig?.sync_error) && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950 rounded-md">
                      <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-red-800 dark:text-red-200">
                        {syncError || ssoConfig?.sync_error}
                      </span>
                    </div>
                  )}

                  {/* Users table */}
                  {ssoConfig && ssoConfig.users.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left p-3 font-medium">User</th>
                            <th className="text-left p-3 font-medium">Roles</th>
                            <th className="text-left p-3 font-medium">Default Role</th>
                            <th className="p-3 w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {ssoConfig.users.map((ssoUser) => {
                            const changeStatus = userChangeStatus.get(ssoUser.email.toLowerCase());
                            return (
                            <tr
                              key={ssoUser.email}
                              className={cn(
                                'border-b last:border-0',
                                changeStatus === 'new' && 'bg-green-50 dark:bg-green-950/30',
                                changeStatus === 'changed' && 'bg-amber-50 dark:bg-amber-950/30',
                              )}
                            >
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{ssoUser.name || ssoUser.email}</span>
                                  {changeStatus === 'new' && (
                                    <Badge variant="outline" className="text-green-700 border-green-300 dark:text-green-400 dark:border-green-700 text-[10px] px-1.5 py-0">New</Badge>
                                  )}
                                  {changeStatus === 'changed' && (
                                    <Badge variant="outline" className="text-amber-700 border-amber-300 dark:text-amber-400 dark:border-amber-700 text-[10px] px-1.5 py-0">Modified</Badge>
                                  )}
                                </div>
                                <div className="text-muted-foreground text-xs">{ssoUser.email}</div>
                              </td>
                              <td className="p-3">
                                <div className="flex flex-wrap gap-1">
                                  {availableRoles.map((role) => (
                                    <Badge
                                      key={role}
                                      variant={ssoUser.snowflake_roles.includes(role) ? 'default' : 'outline'}
                                      className="cursor-pointer text-xs"
                                      onClick={() => toggleRole(ssoUser.email, role)}
                                    >
                                      {role}
                                    </Badge>
                                  ))}
                                </div>
                              </td>
                              <td className="p-3">
                                <Select
                                  value={ssoUser.default_role}
                                  onValueChange={(val) => updateUserDefaultRole(ssoUser.email, val)}
                                >
                                  <SelectTrigger className="w-48">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ssoUser.snowflake_roles.map((role) => (
                                      <SelectItem key={role} value={role}>
                                        {role}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="p-3">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeUser(ssoUser.email)}
                                >
                                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Add user */}
                  {availableUsers.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Select onValueChange={addUser}>
                        <SelectTrigger className="w-72">
                          <SelectValue placeholder="Add a user..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableUsers.map((u) => (
                            <SelectItem key={u.email} value={u.email}>
                              {u.name || u.email} ({u.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-muted-foreground">
                        <Plus className="h-4 w-4 inline" /> Add user from your team
                      </span>
                    </div>
                  )}

                  {/* Action button — differs based on hosted vs non-hosted */}
                  <div className="pt-2">
                    {isHosted ? (
                      <>
                        <Button
                          onClick={saveAndSync}
                          disabled={saving || syncing || !ssoConfig?.users?.length || !isDirty}
                          variant={isDirty ? 'default' : 'outline'}
                        >
                          {syncing ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Syncing...
                            </>
                          ) : isDirty ? (
                            'Save & Sync Changes'
                          ) : (
                            'No Changes to Sync'
                          )}
                        </Button>
                        <p className="text-sm text-muted-foreground mt-2">
                          This will provision the SAML security integration and create federated
                          users in your Snowflake account.
                        </p>
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={saveAndGenerateScript}
                          disabled={saving || generatingScript || !ssoConfig?.users?.length || !isDirty}
                          variant={isDirty ? 'default' : 'outline'}
                        >
                          {generatingScript ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Generating...
                            </>
                          ) : isDirty ? (
                            'Save & Generate Script'
                          ) : (
                            'No Changes to Sync'
                          )}
                        </Button>
                        <p className="text-sm text-muted-foreground mt-2">
                          Generates a SQL script you can run in your Snowflake account to set up
                          the SAML integration and create federated users.
                        </p>
                      </>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
